import type { CaptureSequence, CustomLandmarks, Landmark, PelvisLandmarks3D, PelvisPoint3D } from '../types';
import { IDX } from '../constants';

type Point2D = { x: number; y: number };

type ApFrame = {
  kind: 'ap';
  view: 'Anterior' | 'Posterior';
  originPx: Point2D; // mid-ankles (or best available ankle)
  cmPerPx: number;
};

type LatFrame = {
  kind: 'lat';
  view: 'Left' | 'Right';
  ankleRefPx: Point2D; // ankle point for this view
  cmPerPx: number;
  zSign: 1 | -1; // Right: +1, Left: -1 (so +Z is always anterior)
};

export type ViewFrame = ApFrame | LatFrame;

export type Pelvis2DKey = 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS';

const clonePoint3D = (p: PelvisPoint3D): PelvisPoint3D => ({ x_cm: p.x_cm, y_cm: p.y_cm, z_cm: p.z_cm });

export function emptyPelvis3D(): PelvisLandmarks3D {
  return {
    ASIS_L: { x_cm: null, y_cm: null, z_cm: null },
    ASIS_R: { x_cm: null, y_cm: null, z_cm: null },
    PSIS_L: { x_cm: null, y_cm: null, z_cm: null },
    PSIS_R: { x_cm: null, y_cm: null, z_cm: null },
  };
}

function isValidFrame(frame: ViewFrame | null): frame is ViewFrame {
  return !!frame && isFinite(frame.cmPerPx) && frame.cmPerPx > 0;
}

export function computeViewFrame(view: CaptureSequence, lm: Landmark[] | null | undefined, cmPerPx: number | null | undefined): ViewFrame | null {
  if (!lm || !isFinite(cmPerPx as number) || !cmPerPx || cmPerPx <= 0) return null;

  if (view === 'Anterior' || view === 'Posterior') {
    const aL = lm[IDX.L_ANK];
    const aR = lm[IDX.R_ANK];
    const originPx =
      aL && aR
        ? { x: (aL.x + aR.x) / 2, y: (aL.y + aR.y) / 2 }
        : aL
          ? { x: aL.x, y: aL.y }
          : aR
            ? { x: aR.x, y: aR.y }
            : null;
    if (!originPx) return null;
    return { kind: 'ap', view, originPx, cmPerPx };
  }

  // Lateral: only the ankle on the declared side (no opposite-ankle fallback).
  const ankleIdx = view === 'Right' ? IDX.R_ANK : IDX.L_ANK;
  const a = lm[ankleIdx];
  if (!a) return null;
  return { kind: 'lat', view, ankleRefPx: { x: a.x, y: a.y }, cmPerPx, zSign: view === 'Right' ? 1 : -1 };
}

function apPxToCm(frame: ApFrame, p: Point2D): Pick<PelvisPoint3D, 'x_cm' | 'y_cm'> {
  return {
    x_cm: (p.x - frame.originPx.x) * frame.cmPerPx,
    y_cm: -(p.y - frame.originPx.y) * frame.cmPerPx,
  };
}

function apCmToPx(frame: ApFrame, p: Pick<PelvisPoint3D, 'x_cm' | 'y_cm'>): Point2D | null {
  if (p.x_cm === null || p.y_cm === null) return null;
  return {
    x: frame.originPx.x + p.x_cm / frame.cmPerPx,
    y: frame.originPx.y - p.y_cm / frame.cmPerPx,
  };
}

function latPxToCm(frame: LatFrame, p: Point2D): Pick<PelvisPoint3D, 'z_cm' | 'y_cm'> {
  return {
    z_cm: frame.zSign * (p.x - frame.ankleRefPx.x) * frame.cmPerPx,
    y_cm: -(p.y - frame.ankleRefPx.y) * frame.cmPerPx,
  };
}

function latCmToPx(frame: LatFrame, p: Pick<PelvisPoint3D, 'z_cm' | 'y_cm'>): Point2D | null {
  if (p.z_cm === null || p.y_cm === null) return null;
  return {
    x: frame.ankleRefPx.x + frame.zSign * (p.z_cm / frame.cmPerPx),
    y: frame.ankleRefPx.y - p.y_cm / frame.cmPerPx,
  };
}

export function updatePelvis3DFrom2D(
  pelvis: PelvisLandmarks3D,
  view: CaptureSequence,
  key: Pelvis2DKey,
  px: Point2D,
  frame: ViewFrame
): PelvisLandmarks3D {
  const next: PelvisLandmarks3D = {
    ASIS_L: clonePoint3D(pelvis.ASIS_L),
    ASIS_R: clonePoint3D(pelvis.ASIS_R),
    PSIS_L: clonePoint3D(pelvis.PSIS_L),
    PSIS_R: clonePoint3D(pelvis.PSIS_R),
  };

  const applyAp = (target: PelvisPoint3D) => {
    if (frame.kind !== 'ap') return;
    const { x_cm, y_cm } = apPxToCm(frame, px);
    target.x_cm = x_cm;
    target.y_cm = y_cm;
  };

  const applyLat = (target: PelvisPoint3D) => {
    if (frame.kind !== 'lat') return;
    const { z_cm, y_cm } = latPxToCm(frame, px);
    target.z_cm = z_cm;
    target.y_cm = y_cm;
  };

  if (view === 'Anterior' || view === 'Posterior') {
    if (key === 'L_ASIS') applyAp(next.ASIS_L);
    if (key === 'R_ASIS') applyAp(next.ASIS_R);
    if (key === 'L_PSIS') applyAp(next.PSIS_L);
    if (key === 'R_PSIS') applyAp(next.PSIS_R);
    return next;
  }

  // Lateral (key ASIS/PSIS maps to side-specific point)
  const isRight = view === 'Right';
  if (key === 'ASIS') applyLat(isRight ? next.ASIS_R : next.ASIS_L);
  if (key === 'PSIS') applyLat(isRight ? next.PSIS_R : next.PSIS_L);
  return next;
}

export function projectPelvis3DToCustomByView(pelvis: PelvisLandmarks3D, frames: Partial<Record<CaptureSequence, ViewFrame>>): Partial<Record<CaptureSequence, CustomLandmarks>> {
  const out: Partial<Record<CaptureSequence, CustomLandmarks>> = {};

  const apViews: CaptureSequence[] = ['Anterior', 'Posterior'];
  apViews.forEach((v) => {
    const frame = frames[v];
    if (!frame || frame.kind !== 'ap') return;
    if (v === 'Anterior') {
      const l = apCmToPx(frame, pelvis.ASIS_L);
      const r = apCmToPx(frame, pelvis.ASIS_R);
      out[v] = {
        ...(out[v] || {}),
        ...(l ? { L_ASIS: l } : {}),
        ...(r ? { R_ASIS: r } : {}),
      };
    } else {
      const l = apCmToPx(frame, pelvis.PSIS_L);
      const r = apCmToPx(frame, pelvis.PSIS_R);
      out[v] = {
        ...(out[v] || {}),
        ...(l ? { L_PSIS: l } : {}),
        ...(r ? { R_PSIS: r } : {}),
      };
    }
  });

  const latViews: CaptureSequence[] = ['Left', 'Right'];
  latViews.forEach((v) => {
    const frame = frames[v];
    if (!frame || frame.kind !== 'lat') return;
    const isRight = v === 'Right';
    const asisPx = latCmToPx(frame, isRight ? pelvis.ASIS_R : pelvis.ASIS_L);
    const psisPx = latCmToPx(frame, isRight ? pelvis.PSIS_R : pelvis.PSIS_L);
    out[v] = {
      ...(out[v] || {}),
      ...(asisPx ? { ASIS: asisPx } : {}),
      ...(psisPx ? { PSIS: psisPx } : {}),
    };
  });

  return out;
}

/**
 * Very simple seed: use hip landmarks as proxies for ASIS (same X/Y on AP, same Z/Y on lateral).
 * PSIS is initialized slightly posterior (+/-Z) and slightly superior (Y).
 * This provides an auto-placed, adjustable starting point.
 */
export function seedPelvis3DFromViews(opts: {
  current: PelvisLandmarks3D | null;
  captured: Partial<Record<CaptureSequence, { landmarks: Landmark[]; cmPerPx: number }>>;
}): PelvisLandmarks3D {
  const pelvis = opts.current ? {
    ASIS_L: clonePoint3D(opts.current.ASIS_L),
    ASIS_R: clonePoint3D(opts.current.ASIS_R),
    PSIS_L: clonePoint3D(opts.current.PSIS_L),
    PSIS_R: clonePoint3D(opts.current.PSIS_R),
  } : emptyPelvis3D();

  const maybeUpdateFromAp = (view: 'Anterior' | 'Posterior') => {
    const cap = opts.captured[view];
    if (!cap) return;
    const frame = computeViewFrame(view, cap.landmarks, cap.cmPerPx);
    if (!isValidFrame(frame) || frame.kind !== 'ap') return;
    const lHip = cap.landmarks[IDX.L_HIP];
    const rHip = cap.landmarks[IDX.R_HIP];
    if (lHip) {
      const { x_cm, y_cm } = apPxToCm(frame, lHip);
      pelvis.ASIS_L.x_cm ??= x_cm;
      pelvis.ASIS_L.y_cm ??= y_cm;
      pelvis.PSIS_L.x_cm ??= x_cm;
      if (y_cm !== null) {
        pelvis.PSIS_L.y_cm ??= y_cm + 1.5; // slightly superior
      }
    }
    if (rHip) {
      const { x_cm, y_cm } = apPxToCm(frame, rHip);
      pelvis.ASIS_R.x_cm ??= x_cm;
      pelvis.ASIS_R.y_cm ??= y_cm;
      pelvis.PSIS_R.x_cm ??= x_cm;
      if (y_cm !== null) {
        pelvis.PSIS_R.y_cm ??= y_cm + 1.5;
      }
    }
  };

  const maybeUpdateFromLat = (view: 'Left' | 'Right') => {
    const cap = opts.captured[view];
    if (!cap) return;
    const frame = computeViewFrame(view, cap.landmarks, cap.cmPerPx);
    if (!isValidFrame(frame) || frame.kind !== 'lat') return;

    const isLeft = view === 'Left';
    const hip = cap.landmarks[isLeft ? IDX.L_HIP : IDX.R_HIP];
    if (!hip) return;
    const { z_cm, y_cm } = latPxToCm(frame, hip);
    const asis = isLeft ? pelvis.ASIS_L : pelvis.ASIS_R;
    const psis = isLeft ? pelvis.PSIS_L : pelvis.PSIS_R;

    asis.z_cm ??= z_cm;
    asis.y_cm ??= y_cm;

    if (z_cm !== null) {
      psis.z_cm ??= (z_cm - 6); // ~6cm posterior relative to ASIS proxy
    }
    if (y_cm !== null) {
      psis.y_cm ??= (y_cm + 1.5);
    }
  };

  maybeUpdateFromAp('Anterior');
  maybeUpdateFromAp('Posterior');
  maybeUpdateFromLat('Left');
  maybeUpdateFromLat('Right');

  return pelvis;
}


